// src/utils/api/responseHandler.ts

import { API_TIMEOUTS, RETRY_CONFIG } from './constants';
import { APIError, createAPIError, errorHelpers, type RequestContext } from './errors';
import type { APIResponse, APIRequestConfig } from '../../types/quoter/types';
import { corsConfig } from './corsConfig';
import { requestCache } from './requestCache';
import logger from '../../utils/logging/logger';
import config from '../../config';
const componentName = 'ResponseHandler';

//logger.log('LogResponseFromAPI', componentName, `setComponentStates...`);

class ResponseHandler {
    private static instance: ResponseHandler;
    private activeRequests: Map<string, {
        controller: AbortController;
        timeoutId: NodeJS.Timeout;
        context: RequestContext;
    }>;
    private cleanupInterval: NodeJS.Timeout | null;

    private constructor() {
        this.activeRequests = new Map();
        this.cleanupInterval = setInterval(
            () => this.cleanupStaleRequests(),
            API_TIMEOUTS.CLEANUP_INTERVAL
        );
    }

    public static getInstance(): ResponseHandler {
        if (!ResponseHandler.instance) {
            ResponseHandler.instance = new ResponseHandler();
        }
        return ResponseHandler.instance;
    }

    private generateRequestId(url: string, config: APIRequestConfig): string {
        const method = config.method || 'GET';
        // SECURITY: Never include request body in ID to avoid logging sensitive data like passwords
        const timestamp = Date.now();
        const random = Math.random().toString(36).substring(2, 8);
        return `${method}_${url.split('?')[0]}_${timestamp}_${random}`;
    }

    private createRequestContext(url: string, config: APIRequestConfig): RequestContext {
        return {
            url,
            method: config.method || 'GET',
            timestamp: Date.now(),
            retryAttempt: 0,
            requestId: this.generateRequestId(url, config)
        };
    }

    private cleanupStaleRequests(): void {
        const now = Date.now();
        for (const [id, request] of this.activeRequests.entries()) {
            if (now - request.context.timestamp > API_TIMEOUTS.DEFAULT) {
                console.log('Aborting stale request:', id);
                this.abortRequest(id);
            }
        }
    }

    private abortRequest(id: string): void {
        const request = this.activeRequests.get(id);
        if (request) {
            request.controller.abort();
            clearTimeout(request.timeoutId);
            this.activeRequests.delete(id);
            console.log('Request aborted:', id);
        }
    }

    private async logErrorToDatabase(errorData: {
        logCode: string;
        userId: number;
        logDetail: string;
        actionCode: string;
    }): Promise<void> {
        try {
            // Don't await this - fire and forget to avoid slowing down the main response
            // Map frontend field names to backend expected names
            const backendData = {
                userId: errorData.userId,
                issueCode: errorData.logCode,
                issueDetail: errorData.logDetail
            };
            fetch(`${config.API_URL}/api/Issue-log`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(backendData)
            }).catch(logError => {
                // Silently catch logging errors to avoid infinite loops
                console.warn('Failed to log error to database:', logError);
            });
        } catch (logError) {
            // Silently catch logging errors
            console.warn('Failed to initiate error logging:', logError);
        }
    }

    private async handleResponse<T>(
        response: Response,
        context: RequestContext,
        validateFn?: (data: any) => T
    ): Promise<APIResponse<T>> {
        logger.log('LogResponseFromAPI', componentName,
            `handleResponse - response.status: ${response.status} - response.statusText: ${response.statusText} - headers: ${Object.fromEntries(response.headers.entries())} - context: ${context}`);
        //console.log('Handling response:', {
        //    status: response.status,
        //    statusText: response.statusText,
        //    headers: Object.fromEntries(response.headers.entries()),
        //    context
        //});

        const clonedResponse = response.clone();

        try {
            const contentType = response.headers.get('content-type');

            logger.log('LogResponseFromAPI', componentName, `Response contentType ${contentType}`);
            //console.log('Response content-type:', contentType);

            // Check if response is JSON
            if (!contentType?.includes('application/json')) {
                const textResponse = await response.text();
                console.log('Non-JSON response:', textResponse);
                throw new Error('Response is not JSON');
            }

            const data = await response.json();
            logger.log('LogResponseFromAPI', componentName, `handleResponse - Parsed response data: ${data}`);
            //console.log('responseHandler.handleResponse() Parsed response data:', data);

            if (!response.ok) {
                console.log('Response not OK:', response.status);
                const validationErrors = errorHelpers.extractValidationErrors(data);

                if (validationErrors.length > 0) {
                    console.log('Validation errors found:', validationErrors);
                    throw createAPIError.validation(validationErrors, context);
                }

                // Provide user-friendly messages for authentication errors
                let errorMessage = data.message || data.error;
                if (!errorMessage) {
                    if (response.status === 401) {
                        errorMessage = 'Invalid username or password. Please try again.';
                    } else {
                        errorMessage = `HTTP error! status: ${response.status}`;
                    }
                }
                
                throw createAPIError.http(
                    response.status,
                    errorMessage,
                    context
                );
            }

            if (validateFn) {
                try {
                    //verbose
                    //console.log('Validating response data');
                    const validatedData = validateFn(data);

                    //verbose
                    //console.log('responseHandler.handleResponse() Returning validated data...');
                    return { data: validatedData };
                } catch (validationError) {
                    console.error('Response validation failed:', validationError);
                    throw createAPIError.validation([{
                        field: 'response',
                        message: 'Invalid response format',
                        code: 'INVALID_RESPONSE',
                        value: data
                    }], context);
                }
            }

            logger.log('LogResponseFromAPI', componentName, `handleResponse - Parsed response data: returning...`);
            //console.log('responseHandler.handleResponse() Parsed response data, returning...');

            return { data };
        } catch (err) {
            console.error('Error handling response:', err);

            if (err instanceof APIError) {
                return { error: err };
            }

            try {
                const textData = await clonedResponse.text();
                console.log('Raw response text:', textData.slice(0, 500));

                // Log response parsing errors to database for debugging
                await this.logErrorToDatabase({
                    logCode: 'RESPONSE_ERROR',
                    userId: -1,
                    logDetail: `Response error. Raw response: ${textData.slice(0, 200)}. Error: ${err instanceof Error ? err.message : String(err)}. Context: ${JSON.stringify(context)}`,
                    actionCode: 'API_RESPONSE_ERROR'
                });

                // Return error message with actual details to help diagnose issues
                // Extract any useful error info from the response
                let errorMessage = 'Request failed';
                try {
                    // Try to parse as JSON in case it's a structured error
                    const errorData = JSON.parse(textData);
                    errorMessage = errorData.error || errorData.message || errorMessage;
                } catch {
                    // If not JSON, use raw text (truncated) or original error
                    if (textData && textData.length > 0 && textData.length < 500) {
                        errorMessage = textData;
                    } else if (err instanceof Error) {
                        errorMessage = err.message;
                    }
                }
                
                return {
                    error: createAPIError.parse(
                        errorMessage,
                        context,
                        err instanceof Error ? err : new Error(String(err))
                    )
                };
            } catch (parseError) {
                console.error('Failed to read response text:', parseError);

                // Log parse errors to database as well
                await this.logErrorToDatabase({
                    logCode: 'PARSE_ERROR',
                    userId: -1 || 0,
                    logDetail: `Failed to parse response. Original error: ${err}. Parse error: ${parseError}`,
                    actionCode: 'RESPONSE_PARSE_FAILED'
                });

                return {
                    error: createAPIError.parse(
                        'Failed to process response',
                        context,
                        parseError instanceof Error ? parseError : new Error(String(parseError))
                    )
                };
            }
        }
    }

    private calculateBackoff(attempt: number): number {
        const backoff = Math.min(
            RETRY_CONFIG.INITIAL_DELAY * Math.pow(RETRY_CONFIG.BACKOFF_FACTOR, attempt),
            RETRY_CONFIG.MAX_DELAY
        );
        return backoff * (0.8 + Math.random() * 0.4);
    }

    public async fetch<T>(
        url: string,
        config: APIRequestConfig = {}
    ): Promise<APIResponse<T>> {
        const {
            retries = RETRY_CONFIG.MAX_RETRIES,
            timeout = API_TIMEOUTS.DEFAULT,
            signal,
            headers = {},
            cacheKey,
            skipCache = false,
            validateFn,
            ...requestConfig
        } = config;

        logger.log('LogResponseFromAPI', componentName, `fetch - Fetch request initiated - url: ${url} - method: ${config.method || 'GET'} - headers: ${JSON.stringify(headers)} - hasBody: ${!!config.body}`);

        if (cacheKey && !skipCache) {
            const cachedData = requestCache.get<T>(cacheKey);
            if (cachedData) {
                console.log('Returning cached data for:', cacheKey);
                return { data: cachedData };
            }
        }

        const context = this.createRequestContext(url, config);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            console.log('Request timeout reached:', context.requestId);
            controller.abort();
        }, timeout);

        this.activeRequests.set(context.requestId, {
            controller,
            timeoutId,
            context
        });

        try {
            let lastError: APIError | null = null;

            for (let attempt = 0; attempt <= retries; attempt++) {
                context.retryAttempt = attempt;
                console.log(`Attempt ${attempt + 1}/${retries + 1}:`, context.requestId);

                try {
                    const startTime = Date.now();
                    const fullUrl = url.startsWith('http') ? url : `${config.baseURL || ''}${url}`;

                    //verbose
                    //console.log('Making fetch request:', {
                    //    fullUrl,
                    //    method: requestConfig.method || 'GET',
                    //    headers: { ...corsConfig.headers, ...headers },
                    //    credentials: corsConfig.credentials
                    //});

                    const response = await fetch(fullUrl, {
                        ...corsConfig,
                        ...requestConfig,
                        headers: {
                            ...corsConfig.headers,
                            ...headers
                        },
                        signal: controller.signal
                    });

                    context.duration = Date.now() - startTime;
                    logger.log('LogResponseFromAPI', componentName, `Response received: - response.status ${response.status} - response.statusText ${response.statusText} - context.duration ${context.duration}`);

                    const result = await this.handleResponse<T>(response, context, validateFn);

                    if (result.error) {
                        const apiError = result.error;
                        if (apiError instanceof APIError &&
                            (apiError.category !== 'http' || apiError.status! < 500)) {
                            console.log('Non-retryable error:', apiError);
                            return result;
                        }
                        throw apiError;
                    }

                    if (cacheKey && result.data) {

                        console.log('responseHandler.fetch() cacheKey && result.data...');

                        console.log('Caching response for:', cacheKey);
                        requestCache.set(cacheKey, result.data);
                    }

                    //verbose
                    logger.log('LogResponseFromAPI', componentName, `responseHandler.fetch() returning...`);
                    //console.log('responseHandler.fetch() returning...');

                    return result;
                } catch (err) {
                    console.error(`Attempt ${attempt + 1} failed:`, err);

                    if (err instanceof APIError) {
                        lastError = err;
                    } else {
                        lastError = this.convertError(err);
                    }

                    if (lastError.code === 'ERR_ABORTED' || attempt === retries) {
                        break;
                    }

                    const backoff = this.calculateBackoff(attempt);
                    console.log(`Retrying in ${backoff}ms`);
                    await new Promise(resolve => setTimeout(resolve, backoff));
                }
            }

            console.log('All attempts failed:', lastError);
            return { error: lastError ?? createAPIError.network('Unknown error occurred', context) };
        } finally {
            clearTimeout(timeoutId);
            this.activeRequests.delete(context.requestId);
        }
    }

    private convertError(error: unknown): APIError {
        console.log('Converting error:', error);

        const context: RequestContext = {
            url: '',
            method: '',
            timestamp: Date.now(),
            retryAttempt: 0,
            requestId: 'error-' + Date.now()
        };

        if (error instanceof Error) {
            if (errorHelpers.isCORSError(error)) {
                console.log('CORS error detected');
                return createAPIError.network('CORS error occurred', context, error);
            }
            if (errorHelpers.isNetworkError(error)) {
                console.log('Network error detected');
                return createAPIError.network('Network error occurred', context, error);
            }
            if (errorHelpers.isTimeoutError(error)) {
                console.log('Timeout error detected');
                return createAPIError.timeout(context);
            }
            return createAPIError.network(error.message, context, error);
        }
        return createAPIError.network('Unknown error occurred', context);
    }

    public destroy(): void {
        console.log('Destroying ResponseHandler instance');
        for (const [id] of this.activeRequests) {
            this.abortRequest(id);
        }

        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
            this.cleanupInterval = null;
        }

        ResponseHandler.instance = undefined as any;
    }
}

export const apiHandler = ResponseHandler.getInstance();